import {PurchaseOrder, PurchaseOrderItem} from "../../../api/Logistics";
import {Delivery, DeliveryItem} from "../../../api/Deliveries";
import {forwardRef} from "react";
import {Page, pdfTableStyle} from "../../../misc/pdf/parts/Page";
import moment from "moment";
import {DataSplitInfo, PageRenderProvider} from "../../../misc/pdf/parts/PageContext";
import {PDF} from "../../../misc/pdf/parts/PDF";
import {formatProjectPdfRef, Project} from "../../../api/Projects";
import {table} from "../../../misc/pdf/parts/TableStyle";
import {PDFHeader} from "../../../misc/pdf/parts/PDFHeader";
import {useReadyCheck} from "../../../misc/pdf/UseReadyCheck";
import {buildSections, Row, usePickingSlipData} from "./PickingSlipPDF";
import {Button} from "@mui/material";
import {red} from "@mui/material/colors";

export const PackingSlipPDF = forwardRef(function PackingSlipPDF(props: {
    value: Delivery | PurchaseOrder;
    customerNote?: string;
    items: (DeliveryItem | PurchaseOrderItem)[]
    project: Project;
    signature?: string;
    onReady(): void;

    onCustomerNote?(anchor: any): void;
    onSignature?(anchor: any): void;
}, ref: any) {
    const {rows, loaded} = usePickingSlipData({
        items: props.items,
    })

    const ready = useReadyCheck(props.onReady)
    ready.require(loaded);

    return (
        <div ref={ref}>
            <PDF interactive={!!props.onSignature}>
                <PageRenderProvider data={rows} onReady={ready.section()}>
                    {(info: DataSplitInfo<Row>) => {
                        const lastHeader = rows.slice(0, info.startingDataIndex).findLast((r: Row) => r.type === "header") as Row | undefined;

                        let headerTitle: string | undefined = undefined;
                        if(lastHeader?.type === "header") {
                            headerTitle = lastHeader.title
                        }

                        const rowsByHeader = buildSections(info.rows, headerTitle);

                        return (
                            <Page orientation="portrait">
                                <div style={{flex: 1, display: "flex", flexDirection: "column"}}>
                                {info.pageIndex === 0 && <>
                                    <PDFHeader noAddress title="Packing Slip"/>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        fontSize: "0.8rem",
                                        width: "100%"
                                    }}>
                                        <table className={table}>
                                            <tr>
                                                <td>Project</td>
                                            </tr>
                                            <tr>
                                                <td>{formatProjectPdfRef(props.project.projectNumber)} - {props.project.name}</td>
                                            </tr>
                                            <tr>
                                                <td>Ship To</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <div>{props.project.addressStreet}</div>
                                                    <div>{props.project.addressCity}, {props.project.addressProvince}</div>
                                                    <div>{props.project.addressPostal}</div>
                                                </td>
                                            </tr>
                                        </table>
                                        <div>
                                            <table className={table}>
                                                <tr>
                                                <td>Delivery Date</td>
                                                    <td>S.O. No.</td>
                                                </tr>
                                                <tr>
                                                    <td>{moment().format("MM/DD/YYYY")}</td>
                                                    <td>{props.value.id}</td>
                                                </tr>
                                                <tr>
                                                    <td colSpan={2}>
                                                        Site Contact
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan={2}>
                                                        <div>{props.project.gcContactName}</div>
                                                        <div>{props.project.gcContactPhone}</div>
                                                    </td>
                                                </tr>
                                            </table>
                                        </div>
                                    </div>

                                    <div style={{height: 16}}/>
                                </>}

                                {rowsByHeader.map(section => <>
                                    <div style={{height: 16}}/>
                                    <table className={pdfTableStyle}>
                                        <thead>
                                        <tr>
                                            <th style={{textAlign: "left"}} colSpan={3}>{section.header}</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {section.rows.map((i, index) => {
                                            if (i.type === "header") {
                                                return null;
                                            }

                                            const qtyWidth = 60;
                                            const qty = i.qtyDamagedOrMissing > 0 ?
                                                <div style={{
                                                    display: "flex",
                                                    gap: 4,
                                                    justifyContent: "flex-end",
                                                }}>
                                                    <div style={{
                                                        textDecoration: "line-through",
                                                        color: red["800"],
                                                    }}>{i.qty}</div>
                                                    <div>{i.qty - i.qtyDamagedOrMissing}</div>
                                                </div> : i.qty;

                                            if(!i.openings) {
                                                return (
                                                    <tr key={index.toString()}>
                                                        <td style={{textAlign: "right", width: qtyWidth}}>
                                                            {qty}
                                                        </td>
                                                        <td colSpan={2}>{i.description}</td>
                                                    </tr>
                                                )
                                            }

                                            return (
                                                <tr key={index.toString()}>
                                                    <td style={{textAlign: "right", width: qtyWidth}}>
                                                        {qty}
                                                    </td>
                                                    <td>{i.description}</td>
                                                    <td>
                                                        <div style={{
                                                            display: "flex",
                                                            flexDirection: "row",
                                                            flexWrap: "wrap",
                                                            gap: 4,
                                                            lineHeight: "1em",
                                                        }}>
                                                            {i.openings.map((o, index) => (
                                                                <div style={{
                                                                    textDecoration: o.strikeThrough ? "line-through" : undefined,
                                                                    color: o.strikeThrough ? red["800"] : undefined,
                                                                }} key={index}>{o.name}</div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                        </tbody>
                                    </table>
                                </>)}

                                {info.isLastPage && "customerNote" in props.value && <div>
                                    <div style={{marginTop: 30, fontWeight: "700"}}>Notes</div>
                                    <p style={{whiteSpace: "pre-wrap", lineHeight: "1.4em", margin: 0}}>
                                        {props.customerNote || props.value.customerNote || <>&nbsp;</>}

                                        {props.onCustomerNote && <Button onClick={(e) => props.onCustomerNote?.(e.currentTarget)}>
                                            Add Customer Note
                                        </Button>}
                                    </p>

                                </div>}

                                {info.isLastPage && <div style={{paddingTop: 15, display: "flex", justifyContent: "flex-end"}}>
                                    <div style={{
                                        width: 400, height: 80,
                                        position: "relative",
                                        border: "2px solid black",
                                        backgroundImage: props.signature ? "url(" + props.signature + ")" : undefined,
                                        backgroundPosition: "center center",
                                        backgroundSize: "contain",
                                        backgroundRepeat: "no-repeat",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}>
                                        <div style={{position: "absolute", top: -16, left: 12, padding: 8, paddingTop: 4, backgroundColor: "white"}}>Signature</div>

                                        {props.onSignature && <Button onClick={(e) => props.onSignature?.(e.currentTarget)}>
                                            Tap to sign
                                        </Button>}
                                    </div>
                                </div>}
                                {info.isLastPage && <>
                                    <div style={{flex: 1}} />
                                    <div style={{paddingTop: 15, textAlign: "center"}}>
                                        Any discrepancies or damages must be reported within 48 hours of delivery.
                                    </div>
                                </>}
                            </div>
                        </Page>)
                    }}
                </PageRenderProvider>
            </PDF>
        </div>
    )
});